import { useTeam } from 'app/frontend/base/config/context/team'
import groupBy from 'lodash/groupBy'
import { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { updateMember } from 'app/frontend/base/api/teamMember'
import { displayErrorToast } from 'app/frontend/base/helpers'
import {
  getSpecializationsByClass,
  getRolesWithMultipleSpecs,
} from 'app/frontend/base/services/characterValues'
import { CharacterRole } from 'app/frontend/types/custom'

export const useTeamMemberEditor = (
  member: Partial<wowaudit.TeamMembership>,
) => {
  const { team } = useTeam()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { mutate: updateTeamMember, isPending: isUpdating } = useMutation({
    mutationFn: (data: Partial<wowaudit.TeamMembership>) =>
      updateMember(team.id, member.id as number, data),
    onSuccess: (newData) => {
      toast.success(
        t('dashboard.team.roster.member-updated', {
          name: member.characterReference?.name,
        }),
      )
      queryClient.setQueryData(['team', { path: team.path }], newData)
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  const handleRankChange = (rankId: number) => {
    if (member.teamRankId === rankId) return
    updateTeamMember({ teamRankId: rankId })
  }

  const handleRoleChange = (role: CharacterRole) => {
    if (member.role === role && member.specId === null) return
    updateTeamMember({ role, specId: null })
  }

  const handleSpecChange = (specId: number) => {
    if (member.specId === specId) return
    updateTeamMember({ specId })
  }

  const handleOffSpecChange = (specId: number) => {
    const offspecIds = member.offspecIds || []
    updateTeamMember({
      offspecIds: offspecIds.includes(specId)
        ? offspecIds.filter((id) => id !== specId)
        : [...offspecIds, specId],
    })
  }

  const handleNoteChange = (note: string) => {
    if (member.note === note || (!member.note && !note)) return
    updateTeamMember({ note })
  }

  const classSpecializations = getSpecializationsByClass(
    member.characterReference?.classId,
  )
  const rolesWithMultipleSpecs = getRolesWithMultipleSpecs(
    member.characterReference?.classId,
  )

  return {
    isUpdating,
    handleRankChange,
    handleRoleChange,
    handleSpecChange,
    handleOffSpecChange,
    handleNoteChange,
    classSpecializations,
    rolesWithMultipleSpecs,
  }
}

export const useMembersByRole = (members: wowaudit.TeamMembership[]) => {
  const mains = useMemo(() => {
    return members?.filter((m) => m.teamRank.player_type !== 'alt')
  }, [members])

  const mainsByRole = useMemo(() => {
    return groupBy(
      mains.sort((a, b) => {
        const classCompare = a.characterReference.characterClass.localeCompare(
          b.characterReference.characterClass,
        )
        if (classCompare !== 0) return classCompare
        return a.characterReference.name.localeCompare(
          b.characterReference.name,
        )
      }),
      (m) => m.role,
    )
  }, [mains])

  const reserves = useMemo(() => {
    return members?.filter((m) => m.teamRank.player_type === 'reserve')
  }, [members])

  const reservesByRole = useMemo(() => {
    return groupBy(reserves, (m) => m.role)
  }, [reserves])

  const alts = useMemo(() => {
    return members?.filter((m) => m.teamRank.player_type === 'alt')
  }, [members])

  const altsByRole = useMemo(() => {
    return groupBy(alts, (m) => m.role)
  }, [alts])

  const membersWithOffspecs = useMemo(() => {
    return (
      members?.flatMap((member) =>
        member.offspecDetails
          .filter((offspec) => offspec.role && offspec.role !== member.role)
          .map((offspec) => ({
            member,
            offspecRole: offspec.role,
          })),
      ) ?? []
    )
  }, [members])

  const offSpecsByRole = useMemo(() => {
    return groupBy(membersWithOffspecs, (item) => item.offspecRole)
  }, [membersWithOffspecs])

  return {
    mains,
    mainsByRole,
    reserves,
    reservesByRole,
    alts,
    altsByRole,
    membersWithOffspecs,
    offSpecsByRole,
  }
}

export const useGroupedTeamMembers = (members?: wowaudit.TeamMembership[]) => {
  const { team } = useTeam()
  const teamMembers = members ?? team.members

  const { groupedMembers, userIdsWithDuplicateMains, mainByUser } =
    useMemo(() => {
      const groupByUser = groupBy(
        teamMembers,
        (m) => m.characterReference.userId?.toString() ?? `no-user-${m.id}`,
      )

      const userIdsWithDuplicateMains: number[] = []
      const groupedMembers: wowaudit.TeamMembership[][] = []
      const mainByUser: Record<string, wowaudit.TeamMembership> = {}

      if (!teamMembers) {
        return { groupedMembers, userIdsWithDuplicateMains, mainByUser }
      }

      for (const members of Object.values(groupByUser)) {
        const mains = members.filter((m) => m.teamRank.player_type !== 'alt')
        const alts = members
          .filter((m) => m.teamRank.player_type === 'alt')
          .sort((a, b) =>
            a.characterReference.name.localeCompare(b.characterReference.name),
          )

        if (mains.length === 0) {
          groupedMembers.push(alts)
        } else {
          groupedMembers.push([mains[0], ...alts])

          if (mains[0].characterReference.userId) {
            mainByUser[mains[0].characterReference.userId.toString()] = mains[0]
          }

          if (mains.length > 1 && mains[0].characterReference.userId) {
            userIdsWithDuplicateMains.push(mains[0].characterReference.userId)
          }

          mains.slice(1).forEach((main) => groupedMembers.push([main]))
        }
      }

      return {
        groupedMembers: groupedMembers.sort((leftGroup, rightGroup) =>
          leftGroup[0].characterReference.name.localeCompare(
            rightGroup[0].characterReference.name,
          ),
        ),
        userIdsWithDuplicateMains,
        mainByUser,
      }
    }, [teamMembers])

  return { groupedMembers, userIdsWithDuplicateMains, mainByUser }
}
